import { useListeRegistre } from '../../api/registrePolice.js';
import { useAddons, useFacturesAddons } from '../../api/addons.js';
import { WifiOff } from 'lucide-react';
import {
  Archive,
  KeyRound,
  LifeBuoy,
  ArrowUpRight,
  ChevronRight,
  FileText,
  MapPin,
  Tags,
  Plug,
  Receipt,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  Landmark,
  Users,
} from 'lucide-react';
import type { ComponentType, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ADDONS_IA, moduleDeLaNavigation, type Module } from '@artosera/shared';
import { useShellContext } from '../shell/context.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import styles from './ReglagesPage.module.css';
import { DesignPicker } from '../../appearance/DesignPicker.js';
import { politiqueTheme } from '../../appearance/politique.js';
import { LanguagePicker } from '../../preferences/LanguagePicker.js';
import preferences from '../../preferences/Preferences.module.css';
import { BandeauLectureSeule, CarteReglage, useLectureSeule } from './lectureSeule.js';

interface SectionReglages {
  readonly cle: string;
  readonly icone: ComponentType<{ size?: number; strokeWidth?: number; 'aria-hidden'?: boolean }>;
  /** Chemin complet relatif à la galerie — dans `reglages/…` ou ailleurs (renvoi). */
  readonly vers: string;
  /** Visible si l'une de ces actions est accordée ; toujours visible si absent. */
  readonly actions?: readonly Parameters<typeof galerieALaction>[1][];
  /** Petite indication sous le libellé, pour un renvoi hors de Réglages. */
  readonly indiceCle?: string;
  /** Ce que la section règle : le sous-titre de son écran, repris tel quel. */
  readonly descriptionCle?: string;
  /** Proposée seulement quand ce module est INACTIF (l'archive, sans ventes). */
  readonly seulementSans?: Module;
  /** Proposée seulement quand l'un de ces modules est ACTIF (M0 : modules à connecteur). */
  readonly avecUnDe?: readonly Module[];
}

const SECTIONS: readonly SectionReglages[] = [
  {
    cle: 'tresorerie',
    icone: Landmark,
    vers: 'reglages/tresorerie',
    actions: ['treasury:settings'],
    avecUnDe: ['tresorerie'],
    descriptionCle: 'tresorerie.reglagesDescription',
  },
  {
    cle: 'assurance',
    icone: ShieldCheck,
    vers: 'reglages/assurance',
    actions: ['insurance:read'],
    avecUnDe: ['valeurs-assurance'],
    descriptionCle: 'reglages.assurance.sousTitre',
  },
  {
    cle: 'negociation',
    icone: Receipt,
    vers: 'reglages/negociation',
    actions: ['presentation:read'],
    descriptionCle: 'presentation:reglages.description',
  },
  {
    cle: 'facturesAnterieures',
    icone: Archive,
    vers: 'archive-factures#anterieures',
    actions: ['invoices:read'],
    indiceCle: 'reglages.facturesAnterieures.indice',
  },
  {
    cle: 'horsLigne',
    icone: WifiOff,
    vers: 'reglages/hors-ligne',
    actions: ['artworks:read', 'artists:read', 'exhibitions:read'],
    descriptionCle: 'reglages.horsLigne.sousTitre',
  },
  {
    cle: 'accesApi',
    icone: KeyRound,
    vers: 'reglages/acces-api',
    actions: ['settings:read'],
    descriptionCle: 'reglages.accesApi.sousTitre',
  },
  {
    cle: 'assistantMcp',
    icone: Plug,
    vers: 'reglages/assistant-mcp',
    actions: ['settings:read'],
    descriptionCle: 'reglages.assistantMcp.sousTitre',
  },
  {
    cle: 'assistantRecherche',
    icone: Search,
    vers: 'reglages/assistant-recherche',
    actions: ['recherche:ask'],
    descriptionCle: 'reglages.assistantRecherche.sousTitre',
  },
  {
    cle: 'ia',
    icone: Sparkles,
    vers: 'reglages/ia',
    actions: ['ia:read'],
    avecUnDe: ADDONS_IA,
    descriptionCle: 'reglages.ia.sousTitre',
  },
  {
    cle: 'diffusion',
    icone: Store,
    vers: 'reglages/diffusion',
    actions: ['diffusion:read'],
    descriptionCle: 'diffusion.sousTitre',
  },
  {
    cle: 'securite',
    icone: ShieldCheck,
    vers: 'reglages/securite',
    descriptionCle: 'reglages.securite.sousTitre',
  },
  {
    cle: 'lieux',
    icone: MapPin,
    vers: 'reglages/lieux',
    actions: ['artworks:read'],
    descriptionCle: 'lieux.sousTitre',
  },
  {
    cle: 'typesContact',
    icone: Tags,
    vers: 'reglages/types-contact',
    actions: ['contacts:read'],
    descriptionCle: 'reglages.typesContact.sousTitre',
  },
  {
    cle: 'emplacements',
    icone: MapPin,
    vers: 'reglages/emplacements',
    actions: ['inventaire:read'],
    descriptionCle: 'reglages.emplacements.sousTitre',
  },
  {
    cle: 'equipe',
    icone: Users,
    vers: 'reglages/equipe',
    actions: ['memberships:read'],
    descriptionCle: 'reglages.equipe.sousTitre',
  },
  // Accès de Venio à la galerie : mandat de reprise et accès de support.
  {
    cle: 'accesVenio',
    icone: LifeBuoy,
    vers: 'reglages/acces-venio',
    actions: ['settings:team'],
    descriptionCle: 'reglages.accesVenio.sousTitre',
  },
  // Modèles de documents : le module `documents-prestige` (D21), sorti de
  // Ventes. Sans lui, la carte n'est pas proposée.
  {
    cle: 'documents',
    icone: FileText,
    vers: 'reglages/documents',
    actions: ['settings:templates'],
    descriptionCle: 'reglages.documents.sousTitre',
  },
  // Connecteurs (M0) : seulement si un module qui en consomme est actif.
  {
    cle: 'connecteurs',
    icone: Plug,
    vers: 'reglages/connecteurs',
    actions: ['settings:read'],
    descriptionCle: 'reglages.connecteurs.sousTitre',
    avecUnDe: ['newsletter-reseaux', 'artsy-artnet', 'boutique', 'sso'],
  },

  {
    cle: 'facturation',
    icone: Receipt,
    // Renvoi vers Ventes › Réglages de facturation (ARTO-53, § 3 de l'audit
    // du 23 septembre 2026) : cet écran n'est pas dupliqué ici.
    vers: 'ventes?onglet=reglages',
    actions: ['settings:tax', 'settings:templates'],
    indiceCle: 'reglages.facturation.indice',
  },
  // `facturation` coupée (D21) : les factures et avoirs émis restent consultables.
  {
    cle: 'archiveFactures',
    icone: Archive,
    vers: 'archive-factures',
    actions: ['invoices:read'],
    indiceCle: 'reglages.archiveFactures.indice',
    seulementSans: 'facturation',
  },
];

/** Réglages (3.2, point 11) : galerie, équipe, sécurité — « Sécurité », « Lieux » et « Équipe » (S3-a, ARTO-53) sont construites ici. */
export function ReglagesPage(): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const { galerieSlug, galerie, compte, session } = useShellContext();
  // Consultation hors ligne ou rôle Lecture : cartes classées par `lectureSeule.tsx`.
  const lectureSeule = useLectureSeule(session, galerie);
  const carte = {
    galerieSlug,
    lectureSeule,
    className: styles.carte,
    classeGrisee: styles.carteGrisee,
  };
  const addons = useAddons(galerieSlug, !lectureSeule && galerieALaction(galerie, 'settings:read'));
  const factures = useFacturesAddons(
    galerieSlug,
    !lectureSeule && galerieALaction(galerie, 'addons:invoices'),
  );
  const marketplaceVisible =
    (!addons.isError && addons.data?.marketplaceVisible) ||
    (!factures.isError && factures.data?.marketplaceVisible);
  const registreArchive = useListeRegistre(
    galerieSlug,
    { parPage: 1 },
    true,
    !lectureSeule &&
      !galerieAModule(galerie, 'registre-police') &&
      galerieALaction(galerie, 'registre-police:read'),
  );
  const designLibre = politiqueTheme(galerie).libre;

  return (
    <div className={styles.page}>
      <h1 className={styles.titre}>{t('reglages.titre')}</h1>
      <p className={styles.sousTitre}>{t('reglages.sousTitre')}</p>
      {lectureSeule ? <BandeauLectureSeule raison={lectureSeule} /> : null}

      <ul className={styles.liste}>
        {galerieAModule(galerie, 'prise-rendez-vous') &&
        galerieALaction(galerie, 'creneaux:configurer') ? (
          <CarteReglage vers="reglages/rendez-vous-en-ligne" {...carte}>
            <Receipt size={20} aria-hidden />
            <span className={styles.carteTitre}>{t('priseRendezVous.titre')}</span>
            <ChevronRight size={18} aria-hidden />
          </CarteReglage>
        ) : null}
        {galerieAModule(galerie, 'assistant-reponse') &&
          galerieALaction(galerie, 'inbox:settings') && (
            <CarteReglage vers="reglages/assistant-reponse" {...carte}>
              <span className={styles.carteTitre}>{t('assistantReponse.reglages')}</span>
              <ChevronRight size={18} aria-hidden />
            </CarteReglage>
          )}
        {galerieAModule(galerie, 'boite-reception') && galerieALaction(galerie, 'inbox:read') ? (
          <CarteReglage vers="reglages/boite-reception" {...carte}>
            <span className={styles.carteTitre}>{t('boiteReception.titre')}</span>
            <ChevronRight size={18} aria-hidden />
          </CarteReglage>
        ) : null}
        {galerieAModule(galerie, 'espace-collectionneur') &&
          galerieALaction(galerie, 'collector-space:read') && (
            <CarteReglage vers="reglages/espace-collectionneur" {...carte}>
              <Users size={20} aria-hidden />
              <span className={styles.carteTitre}>{t('espaceCollectionneur.reglages')}</span>
              <ChevronRight size={18} aria-hidden />
            </CarteReglage>
          )}
        {galerieAModule(galerie, 'pipeline-ventes') &&
        galerieALaction(galerie, 'settings:write') &&
        galerieALaction(galerie, 'pipeline:write') ? (
          <CarteReglage vers="reglages/pipeline" {...carte}>
            <Receipt size={20} aria-hidden />
            <span className={styles.carteTitre}>{t('pipeline.titre')}</span>
            <ChevronRight size={18} aria-hidden />
          </CarteReglage>
        ) : null}
        {marketplaceVisible ? (
          <CarteReglage vers="reglages/addons" {...carte}>
            <span className={styles.carteIcone}>
              <Plug size={20} strokeWidth={1.5} aria-hidden />
            </span>
            <span className={styles.carteTitre}>{t('reglages.addons.titre')}</span>
            <ChevronRight size={18} aria-hidden />
          </CarteReglage>
        ) : null}
        {galerieALaction(galerie, 'settings:modules') ? (
          <CarteReglage vers="reglages/modules" {...carte}>
            <Plug size={20} aria-hidden />
            <span className={styles.carteTextes}>
              <span className={styles.carteTitre}>{t('reglages.modules.titre')}</span>
              <span className={styles.carteDescription}>{t('reglages.modules.description')}</span>
            </span>
            <ChevronRight size={18} aria-hidden />
          </CarteReglage>
        ) : null}
        {marketplaceVisible && galerieALaction(galerie, 'addons:billing') ? (
          <CarteReglage vers="reglages/facturation-artosera" {...carte}>
            <Receipt size={20} aria-hidden />
            <span className={styles.carteTitre}>{t('reglages.facturationArtosera.titre')}</span>
            <ChevronRight size={18} aria-hidden />
          </CarteReglage>
        ) : null}
        {galerieAModule(galerie, 'profils-collectionneurs') &&
          galerieALaction(galerie, 'profils:read') && (
            <CarteReglage vers="reglages/profils" {...carte}>
              <span className={styles.carteIcone}>
                <Users size={20} strokeWidth={1.5} aria-hidden />
              </span>
              <span className={styles.carteTextes}>
                <span className={styles.carteTitre}>{t('profils.reglages.titre')}</span>
                <span className={styles.carteDescription}>{t('profils.reglages.description')}</span>
              </span>
              <ChevronRight className={styles.carteFleche} size={18} aria-hidden />
            </CarteReglage>
          )}
        {!galerieAModule(galerie, 'registre-police') &&
          galerieALaction(galerie, 'registre-police:read') &&
          (registreArchive.data?.pagination.total ?? 0) > 0 && (
            <CarteReglage vers="archive-registre-police" {...carte}>
              <span className={styles.carteIcone}>
                <Archive size={20} strokeWidth={1.5} aria-hidden />
              </span>
              <span className={styles.carteTitre}>
                {t('registrePolice.archiveTitre')}
                <small className={styles.carteIndice}>{t('registrePolice.lectureSeule')}</small>
              </span>
              <ChevronRight size={18} aria-hidden />
            </CarteReglage>
          )}
        {SECTIONS.filter((section) => {
          // Un renvoi vers un module complémentaire inactif (D21) — la
          // facturation vit dans Ventes — n'est pas proposé.
          const module = moduleDeLaNavigation(section.vers);
          if (module !== null && !galerieAModule(galerie, module)) return false;
          if (section.seulementSans && galerieAModule(galerie, section.seulementSans)) {
            return false;
          }
          if (
            section.avecUnDe &&
            !section.avecUnDe.some((module) => galerieAModule(galerie, module))
          ) {
            return false;
          }

          return (
            !section.actions || section.actions.some((action) => galerieALaction(galerie, action))
          );
        }).map((section) => {
          const Icone = section.icone;
          // Un renvoi hors de Réglages (Ventes, archive) se signale par une
          // flèche oblique ; une section de Réglages, par un chevron.
          const Fleche = section.vers.startsWith('reglages/') ? ChevronRight : ArrowUpRight;
          if (
            section.avecUnDe &&
            !section.avecUnDe.some((module) => galerieAModule(galerie, module))
          ) {
            return false;
          }

          return (
            <CarteReglage key={section.cle} vers={section.vers} {...carte}>
              <span className={styles.carteIcone}>
                <Icone size={20} strokeWidth={1.5} aria-hidden />
              </span>
              <span className={styles.carteTextes}>
                <span className={styles.carteTitre}>
                  {t(
                    section.cle === 'negociation'
                      ? 'presentation:reglages.titre'
                      : `reglages.${section.cle}.titre`,
                  )}
                </span>
                {section.descriptionCle ? (
                  <span className={styles.carteDescription}>{t(section.descriptionCle)}</span>
                ) : null}
                {section.indiceCle ? (
                  <small className={styles.carteIndice}>{t(section.indiceCle)}</small>
                ) : null}
              </span>
              <Fleche className={styles.carteFleche} size={18} strokeWidth={1.5} aria-hidden />
            </CarteReglage>
          );
        })}
      </ul>

      <section className={preferences.section} aria-labelledby="preferences-title">
        <h2 id="preferences-title">{t('preferences.titre')}</h2>
        <p className={preferences.description}>
          {t(designLibre ? 'preferences.description' : 'preferences.descriptionLangueSeule')}
        </p>
        {/* Lot C : le thème se règle dans la console ; le choix du client peut y être coupé. */}
        {designLibre ? (
          <DesignPicker
            compteId={compte.id}
            langue={i18n.resolvedLanguage === 'en' ? 'en' : 'fr'}
          />
        ) : (
          <p className={preferences.localOnly}>{t('preferences.designImpose')}</p>
        )}
        <LanguagePicker compteId={compte.id} />
        <p className={preferences.localOnly}>{t('preferences.localOnly')}</p>
      </section>
    </div>
  );
}
